import { useCallback, useContext, useMemo } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { DataContext } from '../context/DataContext';
import { WHISPERBI_SECURITY_API } from '../env';

// Mismo patrón exacto que SecurityRolesService.js -- una Política es un
// bundle de permission keys planas del catálogo, adjuntable a Roles y
// Grupos (ver functions/security-roles/app.py::create_policy/update_policy).
const API_BASE_URL = WHISPERBI_SECURITY_API;
const POLICIES_URL = `${API_BASE_URL}/policies`;

async function getIdToken() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    return tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('SecurityPoliciesService.getIdToken: unable to fetch auth session', error);
    return null;
  }
}

async function request(url, options = {}, { requireAuth = true } = {}) {
  const init = { ...options };
  const headers = new Headers(init.headers || {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (requireAuth) {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se encontró un token de autenticación para realizar la operación solicitada.');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  init.headers = headers;
  init.mode = init.mode || 'cors';
  init.cache = init.cache || 'no-cache';

  let response;
  try {
    response = await fetch(url, init);
  } catch (networkError) {
    console.error('SecurityPoliciesService.request: network error', networkError);
    throw new Error('No se pudo conectar con el servicio de políticas. Verifica tu conexión o intenta más tarde.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      const backendMessage = data?.message || data?.error || data?.errorMessage;
      if (backendMessage) {
        errorMessage = backendMessage;
      }
    } catch {
      const text = await response.text().catch(() => '');
      if (text) {
        errorMessage = text;
      }
    }
    throw new Error(errorMessage || 'Error inesperado al comunicar con el servicio de políticas.');
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

export async function listPolicies() {
  return request(POLICIES_URL, { method: 'GET' });
}

export async function createPolicy({ name, description, permissions } = {}) {
  if (!name || !name.trim()) {
    throw new Error('El nombre de la política es obligatorio.');
  }

  const payload = {
    name: name.trim(),
    description: description || '',
    permissions: Array.isArray(permissions) ? permissions : []
  };

  return request(POLICIES_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function updatePolicy(policyId, { name, description, permissions } = {}) {
  if (!policyId) {
    throw new Error('Se requiere el identificador de la política para actualizarla.');
  }

  const payload = {
    name: name ? name.trim() : name,
    description,
    permissions: Array.isArray(permissions) ? permissions : []
  };

  const url = `${POLICIES_URL}/${encodeURIComponent(policyId)}`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
}

export async function deletePolicy(policyId) {
  if (!policyId) {
    throw new Error('Se requiere el identificador de la política para eliminarla.');
  }

  const url = `${POLICIES_URL}/${encodeURIComponent(policyId)}`;
  return request(url, { method: 'DELETE' });
}

function ensurePolicyManagerAccess(isPlatformRoot, isTenantAdmin) {
  if (!isPlatformRoot && !isTenantAdmin) {
    throw new Error('Solo administradores de la cuenta pueden realizar esta operación.');
  }
}

export const useSecurityPoliciesService = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const guard = useCallback(() => {
    ensurePolicyManagerAccess(isPlatformRoot, isTenantAdmin);
  }, [isPlatformRoot, isTenantAdmin]);

  const guardedActions = useMemo(() => ({
    createPolicy: async (payload) => {
      guard();
      return createPolicy(payload);
    },
    updatePolicy: async (policyId, payload) => {
      guard();
      return updatePolicy(policyId, payload);
    },
    deletePolicy: async (policyId) => {
      guard();
      return deletePolicy(policyId);
    }
  }), [guard]);

  return {
    ...guardedActions,
    // Solo lectura: cualquier usuario autenticado puede consultarlas.
    listPolicies
  };
};

const SecurityPoliciesService = {
  listPolicies,
  createPolicy,
  updatePolicy,
  deletePolicy,
  useSecurityPoliciesService
};

export default SecurityPoliciesService;
